import loglevel, { Logger } from 'loglevel';
import { format, subDays } from 'date-fns';
import UAParser from 'ua-parser-js';
import { isBrowser, getGlobalThis } from '@cap/ssr-common-utils';
import { REACT_APP_ENV_MAP } from '../../constants';

/**
 * OWl 错误级别
 */
export enum OWL_ERROR_LEVEL {
  debug = 'debug',
  info = 'info',
  warn = 'warn',
  error = 'error',
}

/**
 * Owl 错误分类
 */
export enum OWL_ERROR_CATEGORY {
  jsError = 'jsError',
  resourceError = 'resourceError',
  ajaxError = 'ajaxError',
}

interface OWLErrorOptions {
  /**
   * 值为false时立刻上报错误，否则读取config中的error对象中的combo配置
   */
  combo?: boolean;
  /**
   * 错误级别，默认error
   */
  level?: OWL_ERROR_LEVEL;
  /**
   * 错误分类，默认jsError
   */
  category?: OWL_ERROR_CATEGORY | string;
  /**
   * 其他错误信息
   */
  tags?: Record<string, unknown>;
}

type ErrorType =
  | Error
  | string
  | {
      /**
       * 对应错误名称(sec_category)
       */
      name: string;
      /**
       * 对应错误日志内容(content)
       */
      msg: string;
    };

export type Navigator = NavigatorNetworkInformation;
export declare interface NavigatorNetworkInformation {
  readonly connection?: NetworkInformation;
  readonly mozConnection?: NetworkInformation;
  readonly webkitConnection?: NetworkInformation;
}
type Megabit = number;
type Millisecond = number;
export type EffectiveConnectionType = '2g' | '3g' | '4g' | 'slow-2g' | 'unknown';
export type ConnectionType =
  | 'bluetooth'
  | 'cellular'
  | 'ethernet'
  | 'mixed'
  | 'none'
  | 'other'
  | 'unknown'
  | 'wifi'
  | 'wimax';
export interface NetworkInformation extends EventTarget {
  readonly type?: ConnectionType;
  readonly effectiveType?: EffectiveConnectionType;
  readonly downlinkMax?: Megabit;
  readonly downlink?: Megabit;
  readonly rtt?: Millisecond;
  readonly saveData?: boolean;
  onchange?: EventListener;
}

/**
 * 将日志上报的Owl
 * @see https://km.sankuai.com/page/1291907878#id-addError
 * @param config
 */
export function reportOwl(error: ErrorType, options?: OWLErrorOptions) {
  if (!isBrowser) return;
  const { combo, level, category, tags } = (options as OWLErrorOptions) || {};

  const opts = {
    combo: combo === true,
    level: level || OWL_ERROR_LEVEL.error,
    category: category || OWL_ERROR_CATEGORY.jsError,
    tags,
  };
  if (typeof window?.Owl?.addError === 'function') {
    window.Owl?.addError(error, opts);
  } else {
    window.owl?.('addError', error, opts);
  }
}

// 钟sir、林长琦、张敬云
const vipList = [30, 135799539, 403530880, 243124876];

/**
 * 上报Owl自定义指标
 * @param {*} key
 * @param {*} duration
 * @param {*} tags
 */
export const reportCustom = (key: string, duration = 0, tags: Record<string, unknown> = {}) => {
  if (!isBrowser) return;
  const ua = navigator.userAgent;
  const { os } = UAParser.UAParser(ua);
  const connection = (navigator as Navigator).connection ||
    (navigator as Navigator).mozConnection ||
    (navigator as Navigator).webkitConnection || { type: 'unknown' };
  const networkUa = ua.match(/NetType\/\w+/);
  const networkTypeUa = (networkUa ? networkUa[0] : 'NetType/other')
    .toLowerCase()
    .replace('nettype/', '');
  let network = '4G';
  if (connection.type === 'wifi' || networkTypeUa === 'wifi') {
    network = 'wifi';
  }
  const metricInst = window.Owl && window.Owl.MetricManager();
  // 自定义指标实例
  const { userId } = tags;
  let vip: number | string = '';
  if (userId) {
    vip = (vipList.indexOf(+userId) > -1 ? userId : '') as string;
  }
  // 增加维度信息
  metricInst &&
    metricInst.setTags({
      os: os.name,
      version: os.version,
      network,
      ...tags,
      vip,
    });
  metricInst && metricInst.setMetric(key, duration);
};

type LoganMessageProps = string | number | boolean | object;

/**
 * 代理 window.Logan 对象上的方法。简化业务使用和判空
 *
 * 各个方法的参数参考：@see https://docs.sankuai.com/logan/user-config/#_7
 */
declare interface Logan {
  /**
   * 日志记录
   * @param logString 日志内容，一次最大1M
   * @param logType
   */
  log(
    logString: string,
    logType?: 'owl' | 'redux' | 'vuex' | 'zaku' | 'archer' | 'mtgame' | 'tracker' | 'default',
  ): void;

  /**
   * Info级别代码级日志
   * @param msg1
   * @param msg2
   */
  info(msg1: LoganMessageProps, ...msg2: LoganMessageProps[]): void;

  /**
   * Warn级别代码级日志
   * @param msg1
   * @param msg2
   */
  warn(msg1: LoganMessageProps, ...msg2: LoganMessageProps[]): void;

  /**
   *  Error级别代码级日志
   * @param {LoganMessageProps} msg1
   * @param {LoganMessageProps[]} msg2
   */
  error(msg1: LoganMessageProps, ...msg2: LoganMessageProps[]): void;

  /**
   * 展示小人侧栏
   */
  showShakeLayer(): void;

  /**
   * 隐藏小人侧栏
   */
  hideShakeLayer(): void;

  /**
   *
   * @param fromDateString 上报从该天开始的日志，可为null，默认为6天前。日期格式：yyyy-MM-DD
   * @param toDateString 上报截止到该天的日志，可为null，默认为今天。日期格式：yyyy-MM-DD
   * @param config 上报环境信息
   */
  report(
    fromDateString: string | null,
    toDateString: string | null,
    config?: { unionId?: string; customReport?: object | string; onReportFinished?: Function },
  ): void;
}

/**
 * 代理 window.Logan 对象上的方法。简化业务使用和判空
 *
 * 各个方法的参数参考：@see https://docs.sankuai.com/logan/user-config/#_7
 *
 * 使用：
 *
 * 1、logan.log(logString, logType);
 *
 * 2、logan.info(msg1 || obj1 [, msg2 || obj2, ...]) Info级别代码级日志
 *
 * 3、logan.warn(msg1 || obj1 [, msg2 || obj2, ...]) Warn级别代码级日志
 *
 * 4、logan.error(msg1 || obj1 [, msg2 || obj2, ...]) Error级别代码级日志
 *
 * 5、logan.showShakeLayer() 展示小人侧栏
 *
 * 6、logan.hideShakeLayer() 展示小人侧栏
 *
 * 7、logan.report(fromDateString, toDateString, config) 日志主动上报
 */
// @ts-ignore
const logan: Logan = [
  'log',
  'info',
  'warn',
  'error',
  'showShakeLayer',
  'hideShakeLayer',
  'report',
].reduce((prev, method) => {
  // @ts-ignore
  prev[method] = (...args) => window.Logan?.[method](...args);
  return prev;
}, {});

/**
 * logan实时日志, 日志立即上报
 * @param {object} logContent 日志对象
 * @param {string} level 默认info
 * @param {string} userId 用户userId, 可选
 */
export const loganRTL = async (logContent: object, level = 'info', userId?: string) => {
  const msg = JSON.stringify({
    ...logContent,
  });
  // @ts-ignore
  logan[level](msg);

  logan.report(format(subDays(new Date(), 1), 'yyyy-MM-dd'), null, {
    unionId: userId ? `rzmt|${userId}` : undefined,
  });
  // error 同时上报到owl
  if (level === 'error') {
    reportOwl(
      {
        // 异常名称
        name: 'logan 实时日志.p3',
        // 异常详细信息
        msg,
      },
      {
        level: OWL_ERROR_LEVEL.warn,
        category: 'logan_error',
      },
    );
  }
};

type LoggerExts = Omit<Logger, 'trace' | 'debug' | 'info' | 'warn' | 'error'> & {
  /**
   * 打印trace日志，同时上报owl
   */
  trace: (error: ErrorType, options?: OWLErrorOptions) => void;
  /**
   * 打印debug日志，同时上报owl
   */
  debug: (error: ErrorType, options?: OWLErrorOptions) => void;

  /**
   * 打印info日志，同时上报owl
   */
  info: (error: ErrorType, options?: OWLErrorOptions) => void;
  /**
   * 打印warn日志，同时上报owl
   */
  warn: (error: ErrorType, options?: OWLErrorOptions) => void;
  /**
   * 打印错误日志，同时上报owl
   */
  error: (error: ErrorType, options?: OWLErrorOptions) => void;
};

const currentLogger = loglevel.getLogger('rzmt');

/**
 * 写入日志，同时上报owl
 * @param { Error | string | { name: string; msg: string; } } error
 * @param {{
 *   combo?: boolean;
 *   level?: 'debug' | 'info' | 'warn' | 'error';
 *   category?: 'jsError' | 'resourceError' | 'ajaxError' | string;
 *   tags?: Record<string, unknown>;
 * }} options
 */
const writeLog = (error: ErrorType, options?: OWLErrorOptions) => {
  // 写入日志之前，先根据环境设置日志的等级
  const that = getGlobalThis();
  if (
    isBrowser &&
    [REACT_APP_ENV_MAP.production].includes(that.runtime_env?.env as REACT_APP_ENV_MAP) &&
    window.location.href.indexOf('__vconsole__=true') === -1
  ) {
    currentLogger.setLevel(currentLogger.levels.INFO);
  } else {
    currentLogger.enableAll();
  }
  const level = options?.level ?? OWL_ERROR_LEVEL.info;
  const enableLevel = currentLogger.getLevel();
  // @ts-ignore
  if (currentLogger.levels[level.toUpperCase()] < enableLevel) return;
  reportOwl(error, { ...options, level });
  let msg;
  if (error instanceof Error) {
    msg = {
      name: error.message,
      msg: error.stack,
    };
  } else if (typeof error === 'string') {
    msg = {
      name: error,
    };
  } else {
    msg = error;
  }

  currentLogger[level]?.(msg, options);
};

// logger 扩展
// @ts-ignore
const logger: LoggerExts = Object.create(currentLogger);

['trace', 'debug', 'info', 'warn', 'error'].reduce((prev, method) => {
  const levelMap = {
    trace: OWL_ERROR_LEVEL.debug,
    log: OWL_ERROR_LEVEL.info,
  };
  // @ts-ignore
  const level = levelMap[method] ?? method;
  // @ts-ignore
  prev[method] = (error: ErrorType, options?: OWLErrorOptions) =>
    writeLog(error, { ...options, level: options?.level || level });
  return prev;
}, logger);

/**
 * 格式化Error类型日志
 * @param error
 * @param args
 * @returns
 */
const formatErrorToString = (error: Error, args?: Record<string, any>) =>
  JSON.stringify({
    message: error?.message,
    stack: error?.stack,
    ...args,
  });

// 全局挂载logger
if (isBrowser) {
  window.logger = logger;
}

export { logger, logan, formatErrorToString };
